import type { JrpcNotification, JrpcRequest, JrpcResponse } from '../../types/general/jrpc';
import { createLogger, type Logger } from '../../utils/logger';

type MessageTransmitter = (payload: string) => void;
type NotificationReceivedHandler = (notification: JrpcNotification) => void;

export class JrpcClient {
  private readonly logger: Logger;
  private readonly messageTransmitter: MessageTransmitter;
  private readonly notificationHandler: NotificationReceivedHandler;
  private requestId = 0;
  private pendingRequests = new Map<
    string | number,
    {
      resolve: (value: JrpcResponse) => void;
      reject: (reason: Error) => void;
    }
  >();

  constructor(
    tag: string = '',
    messageTransmitter: MessageTransmitter,
    notificationHandler: NotificationReceivedHandler
  ) {
    this.logger = createLogger(tag || 'JrpcClient');
    this.messageTransmitter = messageTransmitter;
    this.notificationHandler = notificationHandler;
  }

  handleDisconnect(): void {
    this.pendingRequests.forEach(({ reject }) => {
      reject(new Error('Connection closed'));
    });
    this.pendingRequests.clear();
  }

  handleMessage(data: string): void {
    try {
      this.logger.debug('<=', data);
      const message = JSON.parse(data) as JrpcResponse | JrpcNotification;

      if ('id' in message && message.id !== null) {
        const pending = this.pendingRequests.get(message.id);
        if (pending) {
          this.pendingRequests.delete(message.id);
          pending.resolve(message);
        }
        return;
      }

      if ('method' in message) {
        this.notificationHandler(message);
      }
    } catch {
      this.logger.error('Failed to parse JRPC message:', data);
    }
  }

  async call(method: string, params?: unknown): Promise<JrpcResponse> {
    const id = ++this.requestId;
    const request: JrpcRequest = {
      jsonrpc: '2.0',
      method,
      params,
      id
    };

    return new Promise<JrpcResponse>((resolve, reject) => {
      this.pendingRequests.set(id, {
        resolve,
        reject
      });
      const payload = JSON.stringify(request);
      this.logger.debug('=>', payload);
      this.messageTransmitter(payload);
    });
  }
}
